/**
 * Which pictures on the page are the product.
 *
 * Priority order, per the stage doc: JSON-LD `image[]`, then `og:image`, then
 * `<img>` elements. Retailers serve the same photo at eight sizes, so the
 * interesting work is deciding that eight URLs are one picture — and then
 * keeping the biggest of the eight.
 */

/** Enough for a gallery. Past this it's recommended-products carousels. */
const LIMIT = 12;

/** Under this, in a size the page actually declared, it's furniture for the page. */
const MIN_DECLARED = 400;

/** Structured-data images at or above this count are taken as the whole gallery. */
const TRUSTED_SEEDS = 3;

/**
 * Query params that name a rendition rather than a picture.
 *
 * `f` and `fm` are in here because they cost a real duplicate: IKEA serves
 * `billy.jpg` and `billy.jpg?f=xl` as the same photograph, and without
 * stripping them an import downloads and stores both.
 */
const RESIZE_PARAMS = new Set([
  'w', 'h', 'width', 'height', 'wid', 'hei', 'sw', 'sh', 'size', 'sz',
  'resize', 'fit', 'crop', 'q', 'quality', 'fmt', 'format', 'dpr', 'scl',
  'imwidth', 'imheight', 'im', 'anchor', 'bg', 'auto', 'cs', 'ixlib',
  'f', 'fm', 'qlt', 'sharpen', 'rev', 'v',
]);

/** Filenames that are chrome, not merchandise. */
const JUNK = /logo|sprite|icon|badge|placeholder|blank|spacer|pixel|1x1|loading|swatch/i;

/** Regions of a page that are never the product. */
const FURNITURE = 'header, nav, footer, aside, [role="banner"], [role="navigation"], [role="contentinfo"]';

/**
 * The same idea for shops that don't use the semantic elements. Matched on
 * whole hyphen- or underscore-separated tokens so that `nav-belt` and
 * `site-header` hit while `shadow` and `padding` don't.
 */
const FURNITURE_TOKENS =
  /(^|[-_\s])(nav|navbar|banner|masthead|header|footer|sponsored|advert|ads?|promo|recommend\w*|related|carousel-ad)([-_\s]|$)/i;

type Candidate = {
  url: string;
  /** What the page said this rendition is, when it said anything. */
  declared: number | undefined;
  /**
   * From structured data rather than scraped off an `<img>`.
   *
   * Seeds are never swapped for another rendition. A retailer's Product block
   * usually lists the canonical full-size photo with no size in the URL at
   * all, and "no size stated" must not lose a comparison against an `<img>`
   * that helpfully declares `?w=1200` — that would trade the original for a
   * thumbnail of it.
   */
  seed: boolean;
};

/** A candidate before it's known whether it came from structured data. */
type Source = Omit<Candidate, 'seed'>;

/**
 * `seeds` are the URLs structured data already offered, in its own order.
 * They're trusted: a retailer listing a photo in its Product block is telling
 * you that is the product.
 */
export function candidateImages(
  doc: Document,
  finalUrl: string,
  seeds: readonly string[],
): string[] {
  const fromSeeds = pick(seeds.map((url) => ({ url, declared: undefined, seed: true })), finalUrl);

  // When structured data named a real gallery, that *is* the gallery. Padding
  // it out with whatever else the page renders is how you end up importing
  // five thumbnails of other products — IKEA's BILLY page lists seven photos
  // in its Product block and then shows colour variants and a magazine file
  // in the same markup, and nothing in the HTML distinguishes them.
  //
  // Below three it's likely a site that lists only a hero image, and the
  // scraped candidates are worth having.
  if (fromSeeds.length >= TRUSTED_SEEDS) return fromSeeds.slice(0, LIMIT);

  return pick(
    [...seeds.map((url) => ({ url, declared: undefined, seed: true })), ...fromImgTags(doc)],
    finalUrl,
  ).slice(0, LIMIT);
}

/** Resolve, filter and dedupe a run of candidates, keeping priority order. */
function pick(found: readonly Candidate[], finalUrl: string): string[] {
  const kept = new Map<string, Candidate>();

  for (const candidate of found) {
    const absolute = resolve(candidate.url, finalUrl);
    if (absolute === undefined) continue;

    const key = renditionKey(absolute);
    const known = kept.get(key);

    if (known === undefined) {
      // Insertion order is priority order, and Map preserves it — so a later
      // upgrade keeps the position the first sighting earned.
      kept.set(key, { ...candidate, url: absolute });
      continue;
    }

    // Structured data already named this picture; leave its URL alone.
    if (known.seed) continue;

    // Same picture, seen again. Keep whichever rendition is larger, so the
    // gallery gets the 1200px hero rather than the 200px thumbnail that
    // happened to appear first in the markup.
    if (sizeOf(absolute, candidate.declared) > sizeOf(known.url, known.declared)) {
      kept.set(key, {
        url: absolute,
        declared: candidate.declared ?? known.declared,
        seed: false,
      });
    }
  }

  return [...kept.values()].map((candidate) => candidate.url);
}

/**
 * The stage doc says `<img>` elements "over 400px in their largest srcset
 * candidate". Taken literally that discards nearly everything: most retail
 * pages declare no intrinsic size on the tag at all. So a size is only
 * grounds for *exclusion* when the page actually stated one.
 */
function fromImgTags(doc: Document): Candidate[] {
  const found: Candidate[] = [];

  for (const img of doc.querySelectorAll('img')) {
    // A store's own nav and its sponsored banners are full of large images
    // that look exactly like candidates. Amazon's category masthead was
    // landing as the cover photo of imported items.
    if (inFurniture(img)) continue;

    const best = largestSource(img);
    if (best === undefined) continue;

    // A width in the URL's own resize params is the page stating a size just
    // as plainly as a `width` attribute — `?w=60` on a 200x200 colour swatch
    // is not a product photo. Zero means "said nothing", which is not small.
    const hinted = hintedWidth(best.url);
    const stated = best.declared ?? (hinted > 0 ? hinted : undefined);
    if (stated !== undefined && stated < MIN_DECLARED) continue;

    found.push({ ...best, seed: false });
  }

  return found;
}

/** Whether this image sits inside a region of the page that is never product. */
function inFurniture(img: Element): boolean {
  if (img.closest(FURNITURE) !== null) return true;

  // Walked rather than built into one giant selector: an ancestor whose id or
  // class names it as nav/banner/sponsored is enough, wherever it sits.
  for (let at: Element | null = img; at !== null; at = at.parentElement) {
    const id = at.getAttribute('id');
    if (id !== null && FURNITURE_TOKENS.test(id)) return true;

    const className = at.getAttribute('class');
    if (className !== null && FURNITURE_TOKENS.test(className)) return true;
  }

  return false;
}

/**
 * Lazy loading is the norm on retail, so `src` is often a grey placeholder and
 * the real photo is in `data-src` or `data-srcset`. Both spellings are read,
 * with the srcset forms preferred because they carry widths.
 */
function largestSource(img: Element): Source | undefined {
  const sets = [img.getAttribute('srcset'), img.getAttribute('data-srcset')];

  let best: Source | undefined;
  for (const set of sets) {
    const parsed = largestInSrcset(set);
    if (parsed !== undefined && (best === undefined || width(parsed) > width(best))) {
      best = parsed;
    }
  }
  if (best !== undefined) return best;

  const src = img.getAttribute('data-src') ?? img.getAttribute('src');
  if (src === null || src.trim() === '') return undefined;

  const attr = Number(img.getAttribute('width'));
  return { url: src.trim(), declared: Number.isFinite(attr) && attr > 0 ? attr : undefined };
}

/** "a.jpg 400w, b.jpg 800w" — or the `2x` form, which declares no width. */
function largestInSrcset(srcset: string | null): Source | undefined {
  if (srcset === null || srcset.trim() === '') return undefined;

  let best: Source | undefined;
  let bestRank = -1;

  for (const entry of srcset.split(',')) {
    const [url, descriptor] = entry.trim().split(/\s+/);
    if (url === undefined || url === '') continue;

    const declared = descriptor?.endsWith('w') ? Number(descriptor.slice(0, -1)) : undefined;
    const density = descriptor?.endsWith('x') ? Number(descriptor.slice(0, -1)) : undefined;

    // A width beats a density, and any descriptor beats none.
    const rank =
      Number.isFinite(declared) && declared !== undefined
        ? declared
        : Number.isFinite(density) && density !== undefined
          ? density
          : 0;

    if (rank > bestRank) {
      bestRank = rank;
      best = {
        url,
        declared: Number.isFinite(declared) && declared !== undefined ? declared : undefined,
      };
    }
  }

  return best;
}

function width(source: Source): number {
  return source.declared ?? 0;
}

/** Declared size if there is one, otherwise whatever the URL hints at. */
function sizeOf(url: string, declared: number | undefined): number {
  return declared ?? hintedWidth(url);
}

/**
 * `?imwidth=1200`, `/1200x800/`, `_600x600.jpg`. Not reliable enough to
 * exclude on, but plenty good enough to break a tie between two renditions of
 * a picture already known to be the same.
 */
function hintedWidth(url: string): number {
  let best = 0;

  try {
    const parsed = new URL(url);
    for (const [name, value] of parsed.searchParams) {
      if (!RESIZE_PARAMS.has(name.toLowerCase())) continue;
      const found = Number(value.replace(/\D+/g, ''));
      if (Number.isFinite(found)) best = Math.max(best, found);
    }
  } catch {
    // Unparseable here just means no hint.
  }

  for (const [, size] of url.matchAll(/[/_-](\d{2,4})x\d{2,4}(?=[/._-]|$)/g)) {
    const found = Number(size);
    if (Number.isFinite(found)) best = Math.max(best, found);
  }

  return best;
}

/**
 * The identity of a picture, independent of the size it's being served at:
 * origin and path with size segments neutralised, plus whatever query params
 * weren't about rendering.
 */
function renditionKey(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  const path = parsed.pathname
    .replace(/\/\d{2,4}x\d{2,4}(?=\/|$)/g, '/{size}')
    .replace(/[_-]\d{2,4}x\d{2,4}(?=\.|$)/g, '{size}');

  const kept = [...parsed.searchParams]
    .filter(([name]) => !RESIZE_PARAMS.has(name.toLowerCase()))
    .map(([name, value]) => `${name}=${value}`)
    .sort();

  return `${parsed.origin}${path}${kept.length === 0 ? '' : `?${kept.join('&')}`}`;
}

/**
 * Absolute, web-scheme, and not obviously page furniture. SVG goes too —
 * retail product photography is never SVG, but logos usually are.
 */
function resolve(src: string, base: string): string | undefined {
  let parsed: URL;
  try {
    parsed = new URL(src, base);
  } catch {
    return undefined;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return undefined;
  if (parsed.pathname.endsWith('.svg')) return undefined;
  if (JUNK.test(parsed.pathname)) return undefined;

  return parsed.toString();
}
